/**
 * PPG HRV capture. Tap-to-start, 30s window, fingertip on rear camera + torch.
 * Renders a live waveform + final BPM/RMSSD/quality verdict.
 * Camera frames never persist. Only the final reading is stored locally.
 */
import { openCamera, setTorch, frameToCanvas, meanRedChannel, type CameraStream } from '../chassis/camera.ts';
import { PpgRecorder, interpretHrv, type HrvResult } from '../chassis/ppg.ts';
import { hasConsent, grant } from '../chassis/consent.ts';
import { appendOp } from '../chassis/storage-aaa.ts';

const TARGET_S = 30;

const TPL = /* html */ `
<style>
  :host {
    display: block;
    margin: 28px 0;
    container-type: inline-size;
  }
  .card {
    border: 1px solid oklch(85% 0.02 248 / 0.12);
    border-radius: 14px;
    padding: 22px 24px;
    background:
      linear-gradient(180deg, oklch(12% 0.04 270 / 0.6), oklch(8% 0.03 268 / 0.7)),
      url("data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='1' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.5 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    background-size: cover, 280px 280px;
    background-blend-mode: normal, screen;
    box-shadow:
      inset 0 0 0 1px oklch(80% 0.04 80 / 0.06),
      0 18px 40px -22px oklch(0% 0 0 / 0.85);
  }
  .eyebrow {
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: oklch(70% 0.04 60);
    margin-bottom: 10px;
  }
  h3 {
    margin: 0 0 6px;
    font-family: var(--display);
    font-weight: 500;
    font-size: clamp(22px, 5vw, 30px);
    color: oklch(94% 0.04 88);
    letter-spacing: -0.01em;
  }
  .sub {
    font-family: var(--display);
    font-style: italic;
    color: oklch(72% 0.04 250);
    margin-bottom: 18px;
    font-size: 14px;
  }
  .stage {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
    margin-top: 12px;
  }
  .wave-wrap {
    position: relative;
    height: 96px;
    border-radius: 10px;
    background: oklch(7% 0.02 270 / 0.6);
    overflow: hidden;
    border: 1px solid oklch(70% 0.04 80 / 0.1);
  }
  canvas.wave {
    width: 100%;
    height: 100%;
    display: block;
  }
  .progress-bar {
    height: 3px;
    background: linear-gradient(90deg, oklch(86% 0.14 80), oklch(70% 0.18 50));
    width: 0%;
    transition: width 0.2s linear;
    border-radius: 2px;
    margin-top: 8px;
  }
  .row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-top: 14px;
  }
  .stat {
    border-left: 1px solid oklch(80% 0.04 80 / 0.18);
    padding: 6px 12px;
  }
  .stat-eyebrow {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: oklch(64% 0.04 60);
  }
  .stat-value {
    font-family: var(--display);
    font-size: 26px;
    color: oklch(94% 0.04 88);
    font-weight: 500;
  }
  .stat-value.italic {
    font-style: italic;
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 18px;
  }
  button {
    background: oklch(86% 0.14 82);
    color: oklch(12% 0.04 270);
    border: none;
    padding: 12px 22px;
    border-radius: 999px;
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    cursor: pointer;
    transition: transform 0.12s ease, background 0.12s ease;
  }
  button:hover {
    transform: translateY(-1px);
    background: oklch(90% 0.14 80);
  }
  button[disabled] {
    background: oklch(40% 0.02 270);
    color: oklch(70% 0.04 80);
    cursor: not-allowed;
  }
  button.ghost {
    background: transparent;
    color: oklch(86% 0.04 80);
    border: 1px solid oklch(80% 0.04 80 / 0.25);
  }
  .verdict {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid oklch(80% 0.04 80 / 0.1);
    font-family: var(--display);
    font-style: italic;
    color: oklch(82% 0.05 60);
  }
  .privacy {
    margin-top: 18px;
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: oklch(54% 0.03 60);
    text-align: right;
  }
  .pro-lock {
    margin-top: 12px;
    padding: 12px 14px;
    border: 1px dashed oklch(80% 0.04 80 / 0.25);
    border-radius: 10px;
    font-family: var(--display);
    font-style: italic;
    color: oklch(76% 0.06 60);
    font-size: 14px;
    text-align: center;
  }
  @container (min-width: 520px) {
    .stage { grid-template-columns: 1.4fr 1fr; }
    .row { grid-template-columns: repeat(3, 1fr); }
  }
</style>

<div class="card">
  <div class="eyebrow">Aramgah · HRV Reading · Pro</div>
  <h3>Apni dhadkan se neend ka hisaab</h3>
  <div class="sub">Rear camera + torch + ungli · 30 second · sab kuch is phone par</div>

  <div class="stage">
    <div>
      <div class="wave-wrap">
        <canvas class="wave"></canvas>
      </div>
      <div class="progress-bar" id="prog"></div>
    </div>

    <div class="row">
      <div class="stat">
        <div class="stat-eyebrow">BPM</div>
        <div class="stat-value" id="bpm">—</div>
      </div>
      <div class="stat">
        <div class="stat-eyebrow">RMSSD ms</div>
        <div class="stat-value" id="rmssd">—</div>
      </div>
      <div class="stat">
        <div class="stat-eyebrow">Quality</div>
        <div class="stat-value italic" id="quality">—</div>
      </div>
    </div>
  </div>

  <div class="actions">
    <button id="start">Start reading</button>
    <button id="stop" class="ghost" hidden>Stop</button>
  </div>

  <div class="verdict" id="verdict" hidden></div>
  <div class="pro-lock" id="lock" hidden>Pro feature · ₹999/saal</div>
  <div class="privacy">No video leaves this device</div>
</div>
`;

export class AramgahPpgCapture extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #stream: CameraStream | null = null;
  #recorder = new PpgRecorder();
  #raf = 0;
  #video: HTMLVideoElement | null = null;
  #canvas: HTMLCanvasElement | null = null;
  #wave: HTMLCanvasElement | null = null;
  #timer = 0;
  #waveSamples: number[] = [];

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    this.#wave = this.#shadow.querySelector('canvas.wave');
    const startBtn = this.#shadow.getElementById('start');
    const stopBtn = this.#shadow.getElementById('stop');
    startBtn?.addEventListener('click', () => { void this.#start(); });
    stopBtn?.addEventListener('click', () => { this.#stop(false); });
    if (!isPro()) {
      const lock = this.#shadow.getElementById('lock');
      if (lock) lock.hidden = false;
      if (startBtn) (startBtn as HTMLButtonElement).disabled = true;
    }
  }

  disconnectedCallback(): void {
    this.#stop(false);
  }

  async #start(): Promise<void> {
    if (!hasConsent('camera_ppg')) {
      const ok = confirm('Camera + torch use kar HRV measure karna hai. Sab phone par. Allow?');
      if (!ok) return;
      grant('camera_ppg', 'user accepted HRV PPG prompt');
    }
    try {
      this.#stream = await openCamera({ facingMode: 'environment', width: 320, height: 240 });
      await setTorch(this.#stream.track, true);
      this.#video = document.createElement('video');
      this.#video.srcObject = this.#stream.stream;
      this.#video.playsInline = true;
      this.#video.muted = true;
      await this.#video.play();
      this.#canvas = document.createElement('canvas');
      this.#waveSamples = [];
      this.#recorder.start();
      this.#timer = performance.now();
      this.#tick();
      const startBtn = this.#shadow.getElementById('start') as HTMLButtonElement | null;
      const stopBtn = this.#shadow.getElementById('stop') as HTMLButtonElement | null;
      if (startBtn) startBtn.disabled = true;
      if (stopBtn) stopBtn.hidden = false;
    } catch (e) {
      alert(`Camera failed: ${(e as Error).message}`);
    }
  }

  #tick = (): void => {
    if (!this.#video || !this.#canvas) return;
    const ctx = frameToCanvas(this.#video, this.#canvas);
    if (ctx) {
      const r = meanRedChannel(ctx);
      this.#recorder.push(r);
      this.#waveSamples.push(r);
      if (this.#waveSamples.length > 320) this.#waveSamples.shift();
      this.#drawWave();
      const elapsed = (performance.now() - this.#timer) / 1000;
      this.#updateProgress(elapsed);
      if (elapsed >= TARGET_S) {
        this.#stop(true);
        return;
      }
    }
    this.#raf = requestAnimationFrame(this.#tick);
  };

  #drawWave(): void {
    if (!this.#wave) return;
    const ctx = this.#wave.getContext('2d');
    if (!ctx) return;
    const w = this.#wave.width = this.#wave.clientWidth;
    const h = this.#wave.height = this.#wave.clientHeight;
    ctx.clearRect(0, 0, w, h);
    if (this.#waveSamples.length < 2) return;
    const min = Math.min(...this.#waveSamples);
    const max = Math.max(...this.#waveSamples);
    const range = (max - min) || 1;
    ctx.strokeStyle = 'oklch(86% 0.14 82 / 0.9)';
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    for (let i = 0; i < this.#waveSamples.length; i++) {
      const v = this.#waveSamples[i] ?? 0;
      const x = (i / (this.#waveSamples.length - 1)) * w;
      const y = h - ((v - min) / range) * (h - 4) - 2;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  #updateProgress(elapsed: number): void {
    const bar = this.#shadow.getElementById('prog');
    if (bar) bar.style.width = `${Math.min(100, (elapsed / TARGET_S) * 100)}%`;
  }

  #stop(success: boolean): void {
    cancelAnimationFrame(this.#raf);
    if (this.#stream) {
      void setTorch(this.#stream.track, false);
      this.#stream.stop();
      this.#stream = null;
    }
    this.#video = null;
    this.#canvas = null;
    const startBtn = this.#shadow.getElementById('start') as HTMLButtonElement | null;
    const stopBtn = this.#shadow.getElementById('stop') as HTMLButtonElement | null;
    if (startBtn) startBtn.disabled = false;
    if (stopBtn) stopBtn.hidden = true;
    if (success) {
      const r = this.#recorder.compute();
      this.#renderResult(r);
      void appendOp('aramgah', { path: ['ppg_readings'], value: { ...r, ts: new Date().toISOString() } });
    }
  }

  #renderResult(r: HrvResult): void {
    const bpm = this.#shadow.getElementById('bpm');
    const rmssd = this.#shadow.getElementById('rmssd');
    const quality = this.#shadow.getElementById('quality');
    const verdict = this.#shadow.getElementById('verdict');
    if (bpm) bpm.textContent = r.bpm === 0 ? '—' : String(r.bpm);
    if (rmssd) rmssd.textContent = r.rmssd_ms === 0 ? '—' : String(r.rmssd_ms);
    if (quality) quality.textContent = r.signal_quality;
    const i = interpretHrv(r);
    if (verdict) {
      verdict.hidden = false;
      verdict.textContent = `${i.line_hi} · ${i.line_en}`;
    }
  }
}

function isPro(): boolean {
  return localStorage.getItem('aramgah:tier') === 'pro' || localStorage.getItem('aramgah:tier') === 'family';
}

customElements.define('aramgah-ppg-capture', AramgahPpgCapture);
